import { faUtensils } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { useState, type HTMLAttributes, type PropsWithChildren } from "react";

import { logger } from "~/services/logger";

type ImageCompressProps = {
  width?: string;
  height?: string;
  quality?: string;
};

type ProductImageProps = PropsWithChildren &
  ImageCompressProps & {
    image: string | null;
    alt: string;
    className?: string;
    disabled?: boolean;
  };

export const ProductImage = ({
  image,
  alt,
  children,
  className,
  disabled,
  width,
  height,
  quality
}: ProductImageProps) => {
  const imageSource = getCompressedImageUrl(image, width, height, quality);

  return (
    <div
      className={clsx(
        "relative flex h-56 w-full items-center justify-center",
        className
      )}
    >
      <ImageLoader
        src={imageSource}
        alt={alt}
        className={clsx("size-full rounded-3xl", {
          "opacity-50 grayscale": disabled
        })}
      />
      <div className="absolute inset-0">{children}</div>
    </div>
  );
};

type ImageLoaderProps = HTMLAttributes<HTMLImageElement> & {
  src?: string | null;
  alt: string;
  className?: string;
  fallbackSrc?: string;
};

const ImageLoader = ({ src, alt, className, ...props }: ImageLoaderProps) => {
  const [isLoading, setIsLoading] = useState(true);

  if (!src) {
    return (
      <div
        className={clsx(
          "flex items-center justify-center bg-kiosk-background-secondary",
          className
        )}
      >
        <FontAwesomeIcon icon={faUtensils} color="#DEEBDE" size="6x" />
      </div>
    );
  }

  return (
    <div className="relative size-full">
      <img
        src={src}
        alt={alt}
        onLoad={() => {
          setIsLoading(false);
        }}
        className={clsx("object-cover", className, isLoading && "opacity-0")}
        {...props}
      />
      {isLoading ? (
        <div
          className={clsx(
            "absolute inset-0 animate-pulse bg-background-secondary",
            className
          )}
        />
      ) : null}
    </div>
  );
};

const getCompressedImageUrl = (
  imageUrl: string | null,
  width?: string,
  height?: string,
  quality?: string
) => {
  if (!imageUrl) return null;

  try {
    // Assuming the image URL is a valid URL and we can append a query parameter for compression
    const url = new URL(imageUrl);

    if (quality) {
      url.searchParams.set("q", quality);
    }
    if (width) {
      url.searchParams.set("width", width);
    }
    if (height) {
      url.searchParams.set("height", height);
    }

    return url.toString();
  } catch {
    logger.warn(`Invalid image URL: ${imageUrl}`);
    return imageUrl; // Return the original URL if it fails to parse
  }
};
